import { Button, Heading, Scrollbar } from '@eleveo/components-react';
import { ArrowDownIcon, CrossIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useId } from '@react-aria/utils';
import clsx from 'clsx';
import React from 'react';

import { useLayout } from './LayoutProvider';

/* All will be moved to @eleveo/components-react. */

export type LayoutProps = {
  children: React.ReactNode;
};

// TODO: Try to find better name for this!
export const LayoutWrapper = ({ children }: LayoutProps) => {
  const { nav, sidebar } = useLayout();

  return (
    <div
      className={clsx('layout', {
        'layout--sidebar-open': sidebar.isSidebarOpen,
        'layout--sidebar-extend': sidebar.isSidebarExpanded,
        'layout--nav-collapsed': !nav.isOpen,
      })}
    >
      {children}
    </div>
  );
};

// TODO: Try to find better name for this!
export const NavigationWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="next-layout__navigation">{children}</div>;
};

export type HeaderProps = {
  className?: string;
  title: string;
  testId?: string;
} & React.PropsWithChildren;

export const Header = ({ children, className, testId, title }: HeaderProps) => {
  return (
    <header className={clsx('layout__header', className)}>
      <Heading as="h1" className="header__title" testId={testId}>
        {title}
      </Heading>
      {children}
    </header>
  );
};

export type PanelProps = {
  children: React.ReactNode;
  className?: string;
};

export const Panel = ({ children, className }: PanelProps) => {
  return <div className={clsx('layout__panel', className)}>{children}</div>;
};

export type ContentProps = {
  children: React.ReactNode;
  className?: string;
  testId?: string;
  withPadding?: boolean;
};

export const Content = ({ children, className, testId, withPadding = true }: ContentProps) => {
  let content = children;

  // Padding must be set on inner element to prevent sticky elements from stopping at the padding box.
  if (withPadding) {
    content = <div className="layout__content--padded">{children}</div>;
  }

  return (
    // @ts-expect-error fix in components
    <Scrollbar element="main" className={clsx('layout__content', className)} testId={testId}>
      {content}
    </Scrollbar>
  );
};

// TODO: Try to find better name for this!
export const SidebarWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="layout__sidebar">{children}</div>;
};

export type SidebarProps = {
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
  onExtend?: () => void;
  title: string;
  testId?: string;

  // TODO: make polymorphic component
  as?: React.ElementType;
  [name: string]: any;
};

export const Sidebar = ({
  as: Component = 'div',
  children,
  onClose,
  onExtend,
  title,
  className,
  testId,
  ...props
}: SidebarProps) => {
  const id = useId();

  return (
    // TODO: test screen reader
    <Component
      {...props}
      aria-labelledby={id}
      className={clsx('sidebar', className)}
      data-test-id={testId}
      role="complementary"
    >
      <div className="sidebar__header">
        {onExtend && (
          <Button
            aria-label="Expand/collapse"
            variant="quiet"
            onClick={onExtend}
            className="sidebar__header-button-extend"
          >
            <ArrowDownIcon />
          </Button>
        )}
        <Heading as="h2" className="sidebar__title" id={id}>
          {title}
        </Heading>
        {onClose && (
          <Button aria-label="Close" variant="quiet" onClick={onClose} className="sidebar__header-button-close">
            <CrossIcon />
          </Button>
        )}
      </div>
      {children}
    </Component>
  );
};

export const SidebarPanel = ({ children }: { children: React.ReactNode }) => (
  <div className="sidebar__panel">{children}</div>
);

export type SidebarContentProps = {
  children: React.ReactNode;
  className?: string;
};

export const SidebarContent = ({ children, className }: SidebarContentProps) => (
  <div className={clsx('sidebar__content', className)}>{children}</div>
);

export type SidebarPartProps = {
  children: React.ReactNode;
  className?: string;
};

export const SidebarPart = ({ children, className }: SidebarPartProps) => (
  <Scrollbar className={clsx('sidebar__part', className)}>{children}</Scrollbar>
);
